// V96.11 — Profile detector for the nutrition prompt composer (Phase 3.B).
//
// Maps a client `form` (anamnese) to a list of structured profile tags that
// the composer uses to decide which profile-specific modules to inject into
// the final prompt. Detection is intentionally conservative:
//   - Only triggers a module when the signal is unambiguous in the form.
//   - Returns an ordered list with `primary` first, then comorbidities and
//     context flags.
//
// CRITICAL : tags returned here MUST match exactly the keys exposed by the
// modules registry in ./index.fr.js. Any new module = update both files.
//
// DRAFT V96.11 — detection rules to be reviewed with Anissa before activating
// the composer in production. The default code path (buildSystemPromptFr in
// ../fr.js) is unaffected by this file.
//
// V97.65 — les SEUILS numériques (âges, scores) sont sortis dans le registre
// DB-éditable src/services/clinicalThresholds.js (getThreshold). Les valeurs
// sont inchangées ; elles deviennent éditables par Anissa (prérequis de sa
// session de revue des seuils). Fallback hardcode dans le registre.
import { getThreshold } from '../../../clinicalThresholds.js';

/**
 * @typedef {'femmeCycle' | 'perimenopause' | 'menopause' | 'grossesse' | 'allaitement' | 'postPartum' | 'diabete' | 'digestifChronique' | 'clostridiumDifficile' | 'nephropathie' | 'saos' | 'complicationsDiabete' | 'performanceSportif' | 'thyroide' | 'burnoutCortisol' | 'preConceptionFertilite' | 'spm' | 'endometriose' | 'tdah' | 'sopk'} ProfileTag
 */

/** Parse a numeric field defensively (returns NaN if missing). */
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

/** Normalize a string field to lowercase trimmed. */
function lc(v) {
  return typeof v === 'string' ? v.trim().toLowerCase() : '';
}

/**
 * V96.18 — Calcule l'age depuis une date de naissance ISO (YYYY-MM-DD).
 * Renvoie null si dateNaissance manquante ou invalide. Utilise dans tout le
 * detecteur en remplacement du champ `f.age` brut (qui devient obsolete).
 */
function ageFromDob(dob) {
  if (!dob || typeof dob !== 'string') return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

/** Resolves age from dateNaissance (priority) or falls back to f.age. */
function resolveAge(form) {
  const dobAge = ageFromDob(form?.dateNaissance);
  if (dobAge != null) return dobAge;
  return num(form?.age);
}

/**
 * V97.34 (réconciliation schéma in-app ↔ détecteur, 2026-06-12) — résout
 * l'état maternel depuis DEUX formats d'entrée, sans en privilégier un :
 *   - legacy (cockpit AnissaClientForm / fallback QuestionnaireClient) :
 *     champs SÉPARÉS grossesseActuelle / allaitement / postPartum = "Oui"/"Non".
 *   - in-app (pré-questionnaire app cliente, lib/questionnaire-schema.ts) :
 *     UN champ combiné grossesseActuelle = "Non" | "Grossesse" | "Allaitement"
 *     | "PostPartum".
 *
 * DISPATCH PAR VALEUR, jamais "non-vide" : dans le format in-app le même champ
 * grossesseActuelle porte aussi "Allaitement"/"PostPartum". Un test paresseux
 * `grossesseActuelle !== 'Non'` classerait une femme ALLAITANTE comme enceinte
 * (faux positif inverse, défaut de sécurité dans l'autre sens). On lit donc la
 * valeur explicitement.
 *
 * STRICTEMENT ADDITIF : 'oui' reste mappé vers grossesse (cockpit/fallback
 * legacy préservés) ; on n'ajoute QUE la reconnaissance des libellés combinés.
 * Précédence : grossesse > allaitement > postPartum.
 *
 * @returns {'grossesse'|'allaitement'|'postPartum'|null}
 */
export function resolveMaternalState(form) {
  const f = form || {};
  // Valeur du champ (potentiellement combiné) grossesseActuelle, normalisée
  // (minuscule, sans espaces ni tirets) pour matcher "PostPartum"/"Post-partum".
  const ga = lc(f.grossesseActuelle).replace(/[\s-]/g, '');

  // grossesse — legacy "Oui" OU combiné "Grossesse" OU alias legacy f.grossesse.
  if (ga === 'oui' || ga === 'grossesse' || lc(f.grossesse) === 'oui') {
    return 'grossesse';
  }
  // allaitement — combiné "Allaitement" OU champ legacy séparé f.allaitement.
  if (ga === 'allaitement' || lc(f.allaitement) === 'oui') {
    return 'allaitement';
  }
  // post-partum — combiné "PostPartum" OU champ legacy séparé f.postPartum.
  if (ga === 'postpartum' || lc(f.postPartum) === 'oui') {
    return 'postPartum';
  }
  return null;
}

// ─── Filet texte libre maternel (V97.59, source unique) ─────────────────
//
// Extrait de detectPrimaryFemaleProfile (où il vivait depuis V96.17/V97.34)
// pour être AUSSI consommé par les blocs sécurité toujours actifs FR/EN via
// resolveMaternalStateExtended. Regex FR inchangées au caractère près.
// V97.61 : patterns ANGLAIS ajoutés — liste validée Anissa 2026-07-02
// (réponse au point 6 du doc V2) : pregnant, pregnancy, expecting, trimester,
// breastfeeding (+ breastfeeding mother, couvert), nursing, lactating,
// postpartum / post-partum. « trimester » rejoint le net grossesse FR
// (trimestre) ; « postpartum » était déjà couvert par post.?partum.
const MATERNAL_TEXT_NETS = Object.freeze({
  grossesse: /\benceinte\b|\bgrossesse\b|trimestre\s*[123]|t[123]\s*grossesse|\bsemaine\s*amenorrh|\bpregnan(?:t|cy)\b|\bexpecting\b|\btrimester\b/i,
  allaitement: /\ballait|\bteta?ee|\bteter\b|consultante\s*lactation|\bdiversification|\bbreastfeeding\b|\bnursing\b|\blactating\b/i,
  postPartum: /post.?partum|post.?accouch|apres\s*accouch|sevrage\s*allait|\bcouches\b/i,
});

/** Agrège les champs texte libre scannés par le filet maternel (source unique). */
function collectMaternalFreeText(form) {
  const f = form || {};
  return [
    f.pathologies, f.objectifPrincipalNutrition, f.objectifPrincipal,
    f.objectif_primaire, f.dureeProbleme,
    f.commentaires, f.commentaire, f.notes, f.symptomes, f.contexteEmotionnel,
  ].filter(Boolean).map(lc).join(' | ');
}

/**
 * V97.59 (audit P4.7, point MAJEUR) — état maternel élargi : champs structurés
 * (resolveMaternalState) OU filet regex texte libre. Source unique consommée
 * par buildSafetyBlockFr/En (chemins toujours actifs) ET par le composer
 * (detectPrimaryFemaleProfile). resolveMaternalState reste le résolveur
 * structuré pur.
 *
 * V97.61 — PRÉCÉDENCE : LE STRUCTURÉ GAGNE. Décision Anissa 2026-07-02
 * (réponse au point 4 du doc V2) : « le contexte clinique réel prime sur une
 * simple détection textuelle ». Une fiche marquée Allaitement dont le texte
 * mentionne « poids d'avant grossesse » est ALLAITANTE, pas enceinte.
 * Le filet regex ne sert que lorsqu'AUCUN état structuré n'est posé.
 * Nuance assumée (direction conservatrice) : un « Non » explicite ne résout
 * aucun état structuré → le filet texte s'applique quand même (une mention
 * « enceinte » dans la fiche l'emporte sur un champ potentiellement périmé).
 *
 * @returns {'grossesse'|'allaitement'|'postPartum'|null}
 */
export function resolveMaternalStateExtended(form) {
  const structured = resolveMaternalState(form);
  if (structured) return structured;
  const allText = collectMaternalFreeText(form);
  if (MATERNAL_TEXT_NETS.grossesse.test(allText)) return 'grossesse';
  if (MATERNAL_TEXT_NETS.allaitement.test(allText)) return 'allaitement';
  if (MATERNAL_TEXT_NETS.postPartum.test(allText)) return 'postPartum';
  return null;
}

// ─── Atteinte rénale DÉCLARÉE (V97.61, source unique) ──────────────────
//
// Marqueurs rénaux DÉCLARÉS. V97.66 (décision Anissa, revue des seuils) :
// c'est désormais la SEULE source de suspicion néphropathie — le tag détecteur
// ET le blocage de la clairance (hasDeclaredRenalImpairment) lisent cette même
// regex, alignés (les heuristiques T1 ancienneté/complications sont supprimées).
// Liste validée par Anissa : insuffisance rénale connue, néphropathie, DFG
// diminué, albuminurie, micro-albuminurie, protéinurie, dialyse, ou toute
// mention explicite d'atteinte rénale. NB : la créatinine seule n'est PAS
// incluse (mention banale en biologie, trop de faux positifs) — à confirmer
// avec Anissa si elle veut l'ajouter.
// ─── Réponses structurées qui DÉSIGNENT une situation ───────────────────
//
// 2026-08-01, priorité 1 validée par Anissa : pour ces trois familles, le
// champ structuré fait autorité et le texte libre ne tague plus rien.
//
// Ne figurent ici QUE les réponses diagnostiques spécifiques. Les autres cas
// ne taguent pas, mais ils ne se valent PAS entre eux — la règle corrigée le
// 2026-08-01 par Anissa les sépare en cinq situations distinctes :
//
//   1. réponse diagnostique spécifique → elle décide, elle tague ;
//   2. `aucun` EXPLICITEMENT sélectionné → aucun tag, et aucune validation
//      obligatoire fondée sur une ancienne mention en texte libre. Cette
//      réponse structurée fait autorité ;
//   3. `autre` ou `jeNeSaisPas` → validation obligatoire, MÊME sans mention
//      dans le texte libre ;
//   4. champ non renseigné AVEC une mention correspondante en texte libre →
//      validation obligatoire, jamais d'activation ;
//   5. champ non renseigné SANS mention → aucun tag, aucune validation.
//
// La différence entre 2 et 3-4 est la raison d'être de cette règle : une
// cliente qui a répondu « aucun diagnostic » n'est pas requalifiée, et on ne
// lui réclame pas non plus une clarification, parce qu'un mot traîne dans un
// commentaire écrit des mois plus tôt. Les cas 2 à 5 se distinguent dans
// resolveClinicalState, pas ici : le détecteur ne fait que ne pas taguer.
//
// Ces listes doivent rester alignées sur les options du questionnaire
// (clinicalFieldOptions.js). Un test de cohérence le vérifie.
const DIABETE_SPECIFIQUE = Object.freeze([
  'type1', 'type2', 'gestationnel', 'prediabete', 'insulinoresistance',
]);
const THYROIDE_SPECIFIQUE = Object.freeze([
  'hypothyroidie', 'hashimoto', 'hyperthyroidie', 'basedow',
]);
const DIGESTIF_SPECIFIQUE = Object.freeze([
  'sii', 'rgo', 'crohn', 'rch', 'autremici',
]);

/** Les réponses digestives, en liste, quel que soit le format stocké. */
function valeursDigestives(form) {
  const brut = (form || {}).digestifDiagnostic;
  if (Array.isArray(brut)) return brut.map((v) => String(v ?? '').trim().toLowerCase());
  const v = String(brut ?? '').trim().toLowerCase();
  return v ? [v] : [];
}

const RE_RENAL_DECLARED =/nephropath|insuffisance\s*renale|\birc\b|microalbumin|albuminurie|prot[eé]inurie|egfr|\bdfg\b|dialyse|fonction\s*renale\s*(diminuee|alteree|fragile)|preserver\s*(les|tes)\s*reins|protege.*rein/i;

/** Agrège les champs texte libre scannés par les profils pathologies (source unique). */
function collectPathologyFreeText(form) {
  const f = form || {};
  return [
    f.pathologies, f.objectifPrincipalNutrition, f.objectifPrincipal,
    f.commentaires, f.commentaire, f.notes, f.symptomes,
    f.douleursInflammations, f.troublesPeau, f.contexteEmotionnel,
    f.antecedentsMedicaux, f.medicamentsActuels, f.observations,
  ].filter(Boolean).map(lc).join(' | ');
}

/**
 * Atteinte rénale explicitement déclarée dans l'anamnèse (pas de suspicion
 * heuristique). Consommé par la clairance (blocage validation obligatoire).
 * Pure, fail-safe.
 *
 * @param {object|null|undefined} form
 * @returns {boolean}
 */
export function hasDeclaredRenalImpairment(form) {
  return RE_RENAL_DECLARED.test(collectPathologyFreeText(form));
}

/**
 * Detect the PRIMARY hormonal/life-stage profile (max 1 per client).
 * Order of precedence : grossesse > allaitement > menopause > perimenopause >
 * femmeCycle. Returns null for men or when no primary profile applies.
 *
 * Note V96.11 : grossesse and allaitement modules are not yet shipped — only
 * menopause / perimenopause / femmeCycle are returned here. The detector still
 * checks pregnancy/breastfeeding flags so we can short-circuit and avoid
 * shipping a generic plan that would be unsafe for those clients.
 *
 * @returns {{ tag: ProfileTag | null, blocked: boolean, reason?: string }}
 */
function detectPrimaryFemaleProfile(form) {
  const f = form || {};
  const genre = lc(f.genre);
  if (genre && genre !== 'femme' && genre !== 'female' && genre !== 'f') {
    return { tag: null, blocked: false };
  }

  // V96.17 — Grossesse / allaitement / post-partum : modules dedies disponibles.
  // V96.18 — champs structures `grossesseActuelle`, `allaitement`, `postPartum`
  // prioritaires sur regex texte (anamnese formalisee).
  // V97.34 — objectif_primaire / dureeProbleme (noms émis par le pré-q in-app)
  // ajoutés au scan texte libre EN PLUS de objectifPrincipal (legacy), pas en
  // remplacement : le filet regex (« enceinte » écrit dans l'objectif) était
  // débranché par le décalage de nom. Additif.
  // V97.34 — état maternel résolu depuis legacy ET combiné in-app + filet
  // texte libre. V97.59 : logique extraite dans resolveMaternalStateExtended
  // (source unique partagée avec les blocs sécurité) — comportement identique
  // au caractère près (mêmes regex, même précédence).
  const maternalX = resolveMaternalStateExtended(f);
  if (maternalX === 'grossesse') {
    return { tag: 'grossesse', blocked: false };
  }
  if (maternalX === 'allaitement') {
    return { tag: 'allaitement', blocked: false };
  }
  if (maternalX === 'postPartum') {
    return { tag: 'postPartum', blocked: false };
  }

  // Menopause : explicit flag OR age >= 52 + cycle absent for >= 12 months.
  const menopauseFlag = lc(f.menopause);
  if (menopauseFlag === 'oui' || menopauseFlag === 'confirmee') {
    return { tag: 'menopause', blocked: false };
  }
  const age = resolveAge(f);  // V96.18 : dateNaissance prioritaire sur f.age
  if (age >= getThreshold('menopause_age_min') && lc(f.cycleRegulier) === 'non' && lc(f.derniereRegle) === 'plus_12_mois') {
    return { tag: 'menopause', blocked: false };
  }

  // Perimenopause : 40-55 with hormonal signs (chaleurs, sommeil perturbe,
  // cycle irregulier) OR explicit flag.
  if (lc(f.perimenopause) === 'oui') {
    return { tag: 'perimenopause', blocked: false };
  }
  if (age >= getThreshold('perimenopause_age_min') && age <= getThreshold('perimenopause_age_max')) {
    const signs = [
      lc(f.bouffeesChaleur) === 'oui',
      lc(f.sueursNocturnes) === 'oui',
      lc(f.cycleRegulier) === 'non' || lc(f.cycleRegulier) === 'irregulier',
      lc(f.troubleSommeil) === 'oui',
    ].filter(Boolean).length;
    if (signs >= getThreshold('perimenopause_signs_min')) return { tag: 'perimenopause', blocked: false };
  }

  // Femme en âge de procréer. V97.66 (décision Anissa) : borne haute DÉCOUPLÉE
  // de la ménopause (femme_cycle_age_max, souple à 55) — l'âge oriente, il n'est
  // pas un critère diagnostique. La ménopause se déclenche sur l'aménorrhée
  // (plancher 45), pas sur cette borne.
  if (age != null && age >= getThreshold('femme_cycle_age_min') && age < getThreshold('femme_cycle_age_max')) {
    return { tag: 'femmeCycle', blocked: false };
  }

  return { tag: null, blocked: false };
}

/**
 * Detect comorbidity / pathology modules (additive, max 2 to avoid prompt bloat).
 * Currently shipped : `diabete`, `digestifChronique`.
 *
 * @returns {ProfileTag[]} ordered list, most relevant first.
 */
function detectPathologyProfiles(form) {
  const f = form || {};
  const tags = [];

  // V96.15 — scanner sur plusieurs champs texte libre (pas seulement
  // f.pathologies). Anissa note souvent les comorbidites dans l'objectif,
  // les commentaires ou les symptomes (ex : "hapnee du sommeil niveau 3"
  // saisie dans l'objectif principal pour Melissa).
  const allTextFields = collectPathologyFreeText(f);

  const pathologies = allTextFields; // alias pour les regex existantes
  const objectif = lc(f.objectifPrincipalNutrition || f.objectifPrincipal || '');
  const ballonnements = num(f.frequenceBallonnements);
  // 2026-08-01 : `apneeSommeilNiveau`, `apneeNiveau` et `iah` ne sont plus
  // lus — aucun de ces trois champs n'a jamais existé (voir plus bas, SAOS).

  // V97.4 V3.H quick win — détection glycémie instable depuis les champs
  // structurés (fringalesSucre, variationsGlycemie, reactionGlucides) qui
  // sont collectés étape symptomes mais n'étaient pas exploités. PATTERN
  // COMBINATOIRE (jamais un signal isolé) pour éviter faux positifs :
  //   - fringales quotidiennes OU plusieurs fois par jour
  //   - ET (variations glycémiques marquées OU réaction glucides hors "stable")
  const fringales = lc(f.fringalesSucre);
  const variationsGlyc = lc(f.variationsGlycemie);
  const reactGluc = Array.isArray(f.reactionGlucides)
    ? f.reactionGlucides.map(lc)
    : [];
  const hasStrongCravings = fringales.includes('quotidien') || fringales.includes('plusieurs');
  const hasVariationsMarquees =
    variationsGlyc.includes('permanence') ||
    variationsGlyc.includes('apres les repas') ||
    variationsGlyc.includes('milieu de journee');
  const hasUnstableReaction = reactGluc.some((r) =>
    r.includes('somnolence') || r.includes('faim rapide') || r.includes('ballonnement'),
  );
  const hasGlycemicInstability = hasStrongCravings && (hasVariationsMarquees || hasUnstableReaction);

  // Diabete : T1, T2, prediabete, glycemie, insulinoresistance.
  // V3.H quick win : déclenche aussi sur pattern glycémie instable structuré.
  //
  // 2026-07-31 (étape 1 validée par Anissa) — les jetons NUS `t1` et `t2` sont
  // RETIRÉS de cette alternance : ils matchaient n'importe quel « T1 »/« T2 »
  // du texte libre (vertèbres « douleur T1 », tailles, numérotations) et
  // taguaient la cliente `diabete` à tort. Aucune perte de détection : une
  // vraie mention « diabète T1 » matche déjà par `diabete`. Ne PAS les
  // réintroduire — la distinction T1/T2 passera par le champ structuré
  // `diabeteType`, pas par des mots-clés (cf.
  // docs/MATRICE-SIGNAUX-BLOCAGES-2026-07-31.md).
  // ─── 2026-08-01 · PRIORITÉ 1 : le structuré fait autorité ─────────────
  //
  // Le texte libre ne tague PLUS cette famille. Jusqu'ici, `diabeteDiagnostic`
  // existait mais n'était pas lu ici : le détecteur décidait sur `pathologies`
  // pendant que la couche de blocage décidait sur le champ structuré. Deux
  // couches, deux vérités, sur la même fiche.
  //
  // La règle en trois branches validée par Anissa :
  //   1. réponse structurée présente → elle fait autorité ;
  //   2. question structurée existante mais sans réponse → le texte libre
  //      déclenche une VALIDATION, jamais un module ;
  //   3. aucun champ structuré pour ce domaine → le texte libre reste la
  //      seule source (cas des 8 autres profils, inchangés).
  //
  // Rien n'est perdu par la branche 2 : une mention en texte libre sans
  // réponse structurée produit déjà un motif « à clarifier » qui SUSPEND la
  // génération. Le tag n'aurait servi à rien, aucun plan n'est produit.
  //
  // Les signaux structurés PRATICIENNE sont conservés, sur sa consigne :
  // l'instabilité glycémique reste une source valide.
  const diabeteStructure = DIABETE_SPECIFIQUE.includes(lc(f.diabeteDiagnostic));
  const hasDiabete = diabeteStructure || hasGlycemicInstability;
  if (hasDiabete) {
    tags.push('diabete');
  }

  // V96.14 — Complications du diabete (additif a `diabete`) : retinopathie,
  // cataracte diabetique, neuropathie, mal perforant, calcifications arterielles.
  // On ne le pousse QUE si le module diabete est deja detecte (sinon hors-scope).
  if (hasDiabete && /retinopath|cataracte|neuropath|mal\s*perforant|pied\s*diabetique|calcification|atherosclero/i.test(pathologies)) {
    tags.push('complicationsDiabete');
  }

  // Digestif chronique : SII, MICI, RGO, dysbiose, ou ballonnements SUFFISAMMENT
  // présents. V97.66 (décision Anissa) : seuil >= 3/5 (symptomatologie présente),
  // inversé depuis l'ancien <= 2 qui déclenchait sur des ballonnements faibles.
  //
  // 2026-08-01 · PRIORITÉ 1 : le texte libre ne tague plus cette famille.
  // `digestifDiagnostic` fait autorité, en réponses multiples depuis
  // l'arbitrage 4. Le score de ballonnements, saisi par la praticienne, est
  // une source structurée et reste conservé.
  //
  // Le mot « dysbiose » disparaît donc du déclenchement, ce qui rejoint la
  // règle qu'Anissa avait déjà écrite dans le module : « le terme dysbiose ne
  // déclenche seul aucun protocole ».
  const digestifStructure = valeursDigestives(f).some((v) => DIGESTIF_SPECIFIQUE.includes(v));
  if (digestifStructure
    || (ballonnements && ballonnements >= getThreshold('digestif_ballonnements_min'))) {
    tags.push('digestifChronique');
  }

  // Nephropathie / atteinte rénale. V97.66 (décision Anissa, revue des seuils
  // 2026-07-02) : la suspicion ne repose PLUS sur l'ancienneté du diabète
  // (heuristique supprimée) ni sur les complications microvasculaires — la
  // durée d'un diabète seule ne suffit pas. UNIQUEMENT sur des marqueurs rénaux
  // explicitement déclarés (insuffisance rénale, néphropathie, DFG diminué,
  // albuminurie, micro-albuminurie, protéinurie, dialyse), portés par
  // RE_RENAL_DECLARED. Cette même regex alimente le blocage de la clairance
  // (hasDeclaredRenalImpairment) : détection d'enrichissement et blocage sont
  // désormais alignés sur la même liste (décision Anissa, choix « aligner »).
  if (RE_RENAL_DECLARED.test(pathologies)) {
    tags.push('nephropathie');
  }

  // V96.14 — SAOS / apnee du sommeil.
  //
  // 2026-08-01 : la branche numerique est SUPPRIMEE. Elle lisait
  // `apneeSommeilNiveau`, `apneeNiveau` et `iah` — trois noms pour une meme
  // donnee qui n'existe NULLE PART : ni dans le formulaire praticienne, ni
  // dans le questionnaire cliente, ni dans aucun ecran. Elle ne pouvait donc
  // jamais s'executer.
  //
  // Decision d'Anissa : supprimer plutot que creer un champ pour ressusciter
  // du code. « Si ce module doit un jour etre active, une confirmation
  // structuree cote praticienne sera plus appropriee qu'une deduction
  // numerique ou textuelle automatique. »
  //
  // Le seuil `apnee_iah_min` reste dans le registre des seuils cliniques : il
  // n'est plus consulte par ce detecteur.
  if (/apnee\s*(du)?\s*sommeil|saos|sahos|\biah\b|\bppc\b|\bcpap\b/i.test(pathologies)) {
    tags.push('saos');
  }

  // V96.12 — Clostridioides difficile (CDI) : infection severe post-antibio,
  // recidivante. Detectee prioritairement et independante du `digestifChronique`
  // qui ne couvre pas les specificites infectieuses (rehydratation, recolonisation,
  // garde-fous probiotiques, signaux d'alerte megacolon toxique). On l'insere en
  // tete pour qu'elle soit prioritaire au cap.
  if (/clostridi(?:um|oides)\s*difficile|c\.\s*diff|cdi\b/i.test(pathologies)) {
    tags.unshift('clostridiumDifficile');
  }

  // V96.26 — 8 nouveaux modules suite priorisation Anissa.

  // Performance sportif : objectif performance OU pratique sportive intense.
  // 2026-08-01 : la lecture de `objectifSportif` est SUPPRIMEE, ce champ
  // n'existe nulle part. `frequenceSport`, lui, existe et reste lu.
  if (/performance|musculation|hypertroph|endurance|marathon|triathl|crossfit|powerlift|rugby|football\s*pro/i.test(pathologies + ' ' + objectif)
    || /performance/i.test(lc(f.frequenceSport))) {
    tags.push('performanceSportif');
  }

  // Thyroide / clarte hormonale.
  //
  // 2026-08-01 · PRIORITÉ 1 : `thyroideDiagnostic` fait autorité, le texte
  // libre ne tague plus. C'est la famille où l'incohérence était la plus
  // visible : une cliente ayant répondu « Aucun diagnostic » mais dont un
  // commentaire mentionnait une thyroïdite était taguée quand même, alors que
  // la couche de blocage concluait « aucun ».
  //
  // Aucune source structurée praticienne n'existe pour la thyroïde : cette
  // famille n'a donc qu'un seul déclencheur, contrairement aux deux autres.
  if (THYROIDE_SPECIFIQUE.includes(lc(f.thyroideDiagnostic))) {
    tags.push('thyroide');
  }

  // Burn-out / cortisol chronique : flag explicite OU stress élevé (seuil
  // burnout_stress_min, 7/10 depuis V97.66 — décision Anissa).
  const stress = num(f.niveauStressActuel);
  if (/burn[\s-]?out|surmenage|epuisement\s*professionnel|fatigue\s*chronique\s*severe/i.test(pathologies)
    || stress >= getThreshold('burnout_stress_min')) {
    tags.push('burnoutCortisol');
  }

  // Pre-conception / fertilite / env cellulaire : projet grossesse OUI,
  // parcours PMA, fertilite, IVF, FIV, IAC.
  if (lc(f.projetGrossesse) === 'oui'
    || /pre.?conception|fertilit|infertilit|\bpma\b|\bfiv\b|\biac\b|conception/i.test(pathologies + ' ' + objectif)) {
    tags.push('preConceptionFertilite');
  }

  // SPM : flag explicite OU regles douloureuses notees.
  if (/spm|premenstr|dysmenorrh|regles\s*douloureuses/i.test(pathologies + ' ' + lc(f.spm) + ' ' + lc(f.douleursMenstruelles))
    || (lc(f.spm) && /oui|fort|severe|regulier/i.test(f.spm))
    || (lc(f.douleursMenstruelles) && /oui|fort|severe|regulier/i.test(f.douleursMenstruelles))) {
    tags.push('spm');
  }

  // Endometriose : flag explicite, frequemment sous-diagnostique.
  if (/endometr|adenomyose/i.test(pathologies)) {
    tags.push('endometriose');
  }

  // SOPK : Syndrome des Ovaires Poly-Kystiques. Mots-cles : SOPK, PCOS,
  // ovaires polykystiques, hyperandrogenie, acne adulte severe.
  if (/sopk|\bpcos\b|ovaires?\s*polykyst|hyperandrog/i.test(pathologies)) {
    tags.push('sopk');
  }

  // TDAH / TDA : adulte ou enfant.
  if (/\btdah\b|\btda\b|attention\s*deficit|hyperactiv/i.test(pathologies)) {
    tags.push('tdah');
  }

  // V96.14 — cap monte de 2 a 4 pour permettre les profils multi-comorbidites.
  // V96.17 — cap monte de 4 a 5.
  // V96.26 — cap monte de 5 a 7 pour absorber les profils ultra-complexes
  // (ex: femme avec SOPK + thyroide + burn-out + spm + tdah). Le composer
  // applique un cap final a 8 modules total (1 primary + 7 pathologies).
  return tags.slice(0, 7);
}

/**
 * Top-level detector. Returns the full structured profile a composer can
 * consume. The shape is stable and additive — adding new tags must not
 * remove or rename existing ones.
 *
 * @returns {{
 *   primary: ProfileTag | null,
 *   blocked: boolean,
 *   blockReason?: string,
 *   pathologies: ProfileTag[],
 *   all: ProfileTag[],
 * }}
 */
export function detectClientProfile(form) {
  const primaryRes = detectPrimaryFemaleProfile(form);
  const pathologies = detectPathologyProfiles(form);

  const all = [];
  if (primaryRes.tag) all.push(primaryRes.tag);
  for (const t of pathologies) if (!all.includes(t)) all.push(t);

  return {
    primary: primaryRes.tag,
    blocked: primaryRes.blocked,
    blockReason: primaryRes.reason,
    pathologies,
    all,
  };
}
